import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Typography, Select, Row, Col, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const { Title } = Typography;

export default function OndutyReport() {
    const { authData, backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [onDutyData, setOnDutyData] = useState([]);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    /* ---------------- FETCH MASTER DATA & ON-DUTY RECORDS ---------------- */

    useEffect(() => {
        fetchOrganisations();
        fetchOnDutyRecords();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    const fetchOnDutyRecords = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${backUrl}/api/hr/on-duty-attendance/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setOnDutyData(res.data.results || []);
        } catch (error) {
            console.error("Error fetching on-duty records:", error);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- FILTER HANDLERS ---------------- */

    const handleOrgChange = (value) => {
        setSelectedOrg(value);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDepartments(value);
    };

    const handleDeptChange = (value) => {
        setSelectedDept(value);
        setSelectedDesig(null);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDesignations(value);
    };

    const handleDesigChange = (value) => {
        setSelectedDesig(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    /* ---------------- TABLE DATA MAPPING ---------------- */

    const tableData = useMemo(() => {
        return onDutyData.map(item => {
            const dutyDate = item.date || item.check_in_time;
            const dateObj = dayjs(dutyDate);

            return {
                key: item.id,

                organisationId: item.employee?.organisation,
                departmentId: item.employee?.department,
                designationId: item.employee?.designation,

                monthYear: dateObj.format("MMMM-YYYY"),
                empId: item.employee?.emp_code || item.employee?.user?.username || "-",
                empName: `${item.employee?.user?.first_name || item.employee?.first_name || ""} ${item.employee?.user?.last_name || item.employee?.last_name || ""}`.trim() || "-",
                designationName: item.employee?.designation_name || "-",
                departmentName: item.employee?.department_name || "-",
                site: item.employee?.location || "-",
                onDutyType: item.check_in_work_site === "ON-DUTY"
                    ? "ON-DUTY"
                    : (item.onduty_type_check_in || item.onduty_type_check_out || "-"),
                fromDate: dateObj.format("DD-MM-YYYY"),
                toDate: dateObj.format("DD-MM-YYYY"),
                totalDays: 1,
                purpose: item?.check_in_remarks, // No purpose field in current API response
                approvedBy: "-", // No approval field in current API response
                remarks: "-", // No remarks field in current API response
                mode: item?.check_in_out_mode
            };
        });
    }, [onDutyData]);

    /* ---------------- APPLY FILTERS ---------------- */

    const filteredTableData = useMemo(() => {
        return tableData.filter(row => {
            if (selectedOrg && row.organisationId !== selectedOrg) return false;
            if (selectedDept && row.departmentId !== selectedDept) return false;
            if (selectedDesig && row.designationId !== selectedDesig) return false;

            if (searchText.trim()) {
                const searchLower = searchText.toLowerCase().trim();
                const empIdMatch = row.empId.toLowerCase().includes(searchLower);
                const empNameMatch = row.empName.toLowerCase().includes(searchLower);
                if (!empIdMatch && !empNameMatch) return false;
            }

            return true;
        });
    }, [tableData, selectedOrg, selectedDept, selectedDesig, searchText]);

    /* ---------------- TABLE COLUMNS (Exact Match to Required Format) ---------------- */

    const columns = [
        {
            title: "S.No",
            render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
            width: 70,
            fixed: "left"
        },
        { title: "Month–Year", dataIndex: "monthYear", width: 120 },
        { title: "Employee ID", dataIndex: "empId", width: 130 },
        { title: "Employee Name", dataIndex: "empName", width: 180 },
        { title: "Designation", dataIndex: "designationName", width: 180 },
        { title: "Department", dataIndex: "departmentName", width: 160 },
        { title: "Site/Location", dataIndex: "site", width: 140 },
        { title: "On Duty Type", dataIndex: "onDutyType", width: 140 },
        { title: "On Duty Mode", dataIndex: "mode", width: 140 },
        { title: "From Date", dataIndex: "fromDate", width: 120 },
        { title: "To Date", dataIndex: "toDate", width: 120 },
        // { title: "Total Days", dataIndex: "totalDays", width: 110 },
        { title: "Purpose/Work Details", dataIndex: "purpose", width: 250 },
        // { title: "Approved By", dataIndex: "approvedBy", width: 160 },
        // { title: "Remarks/Description", dataIndex: "remarks", width: 250 }
    ];

    /* ---------------- EXCEL DOWNLOAD (Exact Column Match) ---------------- */

    const downloadExcel = () => {
        const excelData = filteredTableData.map((row, index) => ({
            "S.No": index + 1,
            "Month–Year": row.monthYear,
            "Employee ID": row.empId,
            "Employee Name": row.empName,
            "Designation": row.designationName,
            "Department": row.departmentName,
            "Site/Location": row.site,
            "On Duty Type": row.onDutyType,
            "On Duty Mode": row.mode,
            "From Date": row.fromDate,
            "To Date": row.toDate,
            "Purpose/Work Details": row.purpose,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "OnDuty Report");

        // Style header row
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                fill: { fgColor: { rgb: "0e497b" } },
                font: { color: { rgb: "FFFFFF" }, bold: true },
                alignment: { horizontal: "center" }
            };
        }

        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), "OnDuty_Report.xlsx");
    };

    return (
        <>
            <Title level={4}>On-Duty Report</Title>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={5}>
                    <Select
                        placeholder="Select Organisation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={selectedOrg}
                        onChange={handleOrgChange}
                        style={{ width: "100%" }}
                    >
                        {organisations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedOrg}
                        value={selectedDept}
                        onChange={handleDeptChange}
                        style={{ width: "100%" }}
                    >
                        {departments.map(dept => (
                            <Select.Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedDept}
                        value={selectedDesig}
                        onChange={handleDesigChange}
                        style={{ width: "100%" }}
                    >
                        {designations.map(des => (
                            <Select.Option key={des.id} value={des.id}>
                                {des.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Input
                        placeholder="Search by Employee ID or Name"
                        allowClear
                        value={searchText}
                        onChange={handleSearchChange}
                        style={{ width: "100%" }}
                    />
                </Col>

                <Col span={4} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        style={{ backgroundColor: "#0e497b" }}
                    >
                        Download Excel
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={filteredTableData}
                loading={loading}
                bordered
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: filteredTableData.length,
                    onChange: page => setCurrentPage(page),
                    showSizeChanger: false
                }}
                scroll={{ x: "max-content" }}
                components={{
                    header: {
                        cell: props => (
                            <th {...props} style={{ background: "#0e497b", color: "#fff", textAlign: "center" }}>
                                {props.children}
                            </th>
                        )
                    }
                }}
            />
        </>
    );
}